// Страница статистики студента: история прохождений упражнений с результатами и фильтрацией.

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Badge } from 'react-bootstrap';
import AppNavbar from '../../components/common/AppNavbar';
import { getStudentHistory, prepareRecommendationRequest } from '../../api/studentApi';

/* ─── Вспомогательные константы ─── */

const LEVEL_VARIANT = { 1: 'success', 2: 'info', 3: 'primary', 4: 'warning' };
const LEVEL_LABEL = {
  1: 'Beginner',
  2: 'Elementary',
  3: 'Pre-Intermediate',
  4: 'Intermediate',
};

/* ─── Вспомогательные функции ─── */

function scoreColor(percent) {
  if (percent >= 70) return '#198754';
  if (percent >= 50) return '#0d6efd';
  return '#dc3545';
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const day = d.getDate();
  const months = [
    'янв', 'фев', 'мар', 'апр', 'май', 'июн',
    'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
  ];
  const mon = months[d.getMonth()];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${mon}, ${hh}:${mm}`;
}

function DotProgress({ correct, total, percent }) {
  if (!total || total > 10) {
    return (
      <span className="fw-semibold" style={{ fontSize: '0.8125rem', color: scoreColor(percent) }}>
        {percent}%
      </span>
    );
  }
  return (
    <span className="ewl-dot-progress">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`ewl-dot ${i < correct ? 'ewl-dot--filled' : 'ewl-dot--empty'}`}
        />
      ))}
    </span>
  );
}

/* ─── Карточка одной записи истории ─── */

function HistoryItem({ item, onAction }) {
  const isIncomplete = item.status === 0;
  const hasResult =
    item.status === 1 && item.correct_count != null && item.total_questions != null;

  return (
    <Card
      className={`border-0 ewl-history-card${isIncomplete ? ' ewl-history-card--incomplete' : ''}`}
      style={{
        background: '#fff',
        borderRadius: '0.75rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        marginBottom: 0,
      }}
    >
      <Card.Body className="p-3">
        <div className="d-flex align-items-start gap-3">

          {/* Левая часть: название, тип, прогресс */}
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            {/* Строка 1: название + бейдж уровня */}
            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
              <span
                className="fw-semibold"
                style={{ color: '#1e2a4a', fontSize: '0.9rem', lineHeight: 1.4 }}
              >
                {item.exercise_name}
              </span>
              {item.hard_level && (
                <Badge
                  bg={LEVEL_VARIANT[item.hard_level] ?? 'secondary'}
                  style={{ fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
                >
                  {LEVEL_LABEL[item.hard_level] ?? `Ур. ${item.hard_level}`}
                </Badge>
              )}
            </div>

            {/* Строка 2: тип · дата */}
            <div className="d-flex align-items-center gap-1 flex-wrap">
              {item.exercise_type_name && (
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {item.exercise_type_name}
                </span>
              )}
              {item.exercise_type_name && (
                <span className="text-muted" style={{ fontSize: '0.8rem', opacity: 0.5 }}>·</span>
              )}
              <span className="text-muted" style={{ fontSize: '0.8rem', opacity: 0.75 }}>
                {formatDateTime(item.created_at)}
              </span>
            </div>

            {/* Строка 3: точечный прогресс + «X из Y» (только если есть результат) */}
            {hasResult && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <DotProgress
                  correct={item.correct_count}
                  total={item.total_questions}
                  percent={item.score_percent}
                />
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {item.correct_count} из {item.total_questions}
                </span>
              </div>
            )}
          </div>

          {/* Правая часть: процент / бейдж + кнопка */}
          <div
            className="d-flex flex-column align-items-end gap-2"
            style={{ flexShrink: 0, textAlign: 'right' }}
          >
            {isIncomplete ? (
              <Badge
                bg="warning"
                text="dark"
                style={{ fontSize: '0.68rem', fontWeight: 600 }}
              >
                Не завершено
              </Badge>
            ) : (
              hasResult && (
                <span
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: scoreColor(item.score_percent),
                    lineHeight: 1,
                  }}
                >
                  {item.score_percent}%
                </span>
              )
            )}
            <Button
              size="sm"
              variant={isIncomplete ? 'primary' : 'outline-primary'}
              onClick={() => onAction(item.exercise_id)}
              style={{ borderRadius: '0.4rem', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {isIncomplete ? 'Продолжить' : 'Повторить'}
            </Button>
          </div>

        </div>
      </Card.Body>
    </Card>
  );
}

/* ─── Сводная карточка ─── */

function SummaryCard({ value, label, sub }) {
  return (
    <Card
      className="border-0 text-center"
      style={{
        borderRadius: '0.875rem',
        boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        background: '#fff',
      }}
    >
      <Card.Body className="py-3">
        <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e2a4a' }}>{value}</div>
        <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.25rem' }}>{label}</div>
        {sub != null && (
          <div style={{ fontSize: '0.78rem', color: '#868e96', marginTop: '0.2rem' }}>{sub}</div>
        )}
      </Card.Body>
    </Card>
  );
}

/* ─── Главный компонент страницы ─── */

const PAGE_SIZE = 20;

export default function StudentStatisticsPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [requestingRec, setRequestingRec] = useState(false);
  const [recStatus, setRecStatus] = useState(null); // 'ok' | 'error'

  const loadHistory = useCallback((currentOffset, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    getStudentHistory(PAGE_SIZE, currentOffset)
      .then(({ data }) => {
        setHistory((prev) => (append ? [...prev, ...data] : data));
        setHasMore(data.length === PAGE_SIZE);
        setOffset(currentOffset + data.length);
      })
      .catch(() => setError('Не удалось загрузить историю'))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    loadHistory(0, false);
  }, [loadHistory]);

  const handleLoadMore = () => {
    loadHistory(offset, true);
  };

  const handleAction = (exerciseId) => {
    navigate(`/student/exercises/${exerciseId}`);
  };

  /* ── Вычисляем сводку из загруженных данных ── */
  const completed = history.filter((h) => h.status === 1);
  const noErrors = completed.filter((h) => h.errors_count === 0).length;

  const completedWithData = completed.filter(
    (h) => h.total_questions != null && h.errors_count != null
  );
  const totalSentences = completedWithData.reduce((sum, h) => sum + h.total_questions, 0);
  const errorSentences = completedWithData.reduce((sum, h) => sum + h.errors_count, 0);
  const correctPercent =
    totalSentences > 0 ? Math.round(((totalSentences - errorSentences) / totalSentences) * 100) : null;

  return (
    <div style={{ background: 'var(--ewl-bg-body)', minHeight: '100vh' }}>
      <AppNavbar />

      <Container className="py-4">
        {/* Заголовок */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h4
            className="fw-bold mb-0"
            style={{ color: '#1e2a4a', letterSpacing: '-0.02em' }}
          >
            Моя статистика
          </h4>
          <Button
            size="sm"
            disabled={requestingRec}
            onClick={async () => {
              setRequestingRec(true);
              setRecStatus(null);
              try {
                await prepareRecommendationRequest();
                setRecStatus('ok');
              } catch {
                setRecStatus('error');
              } finally {
                setRequestingRec(false);
              }
            }}
            style={{
              background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
            }}
          >
            {requestingRec
              ? <><Spinner animation="border" size="sm" className="me-1" />Формируем...</>
              : recStatus === 'ok'
              ? 'Запрос отправлен ✓'
              : recStatus === 'error'
              ? 'Ошибка — повторить'
              : 'Получить рекомендации'}
          </Button>
        </div>

        {/* Сводные карточки */}
        {!loading && !error && (
          <Row className="g-3 mb-4">
            <Col xs={12} sm={6}>
              <SummaryCard
                value={completed.length}
                label="Завершено упражнений"
                sub={completed.length > 0 ? `из них без ошибок: ${noErrors}` : null}
              />
            </Col>
            <Col xs={12} sm={6}>
              <SummaryCard
                value={correctPercent !== null ? `${correctPercent}%` : '—'}
                label="Средний % правильных"
                sub={
                  totalSentences > 0
                    ? `${totalSentences - errorSentences} из ${totalSentences} правильно`
                    : null
                }
              />
            </Col>
          </Row>
        )}

        {/* История упражнений */}
        <div
          className="mb-3 pb-2"
          style={{ borderBottom: '1px solid #e9ecef' }}
        >
          <h6
            className="fw-bold mb-0"
            style={{ color: '#1e2a4a', fontSize: '1rem', letterSpacing: '-0.01em' }}
          >
            История упражнений
          </h6>
        </div>

        {/* Загрузка */}
        {loading && (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {/* Ошибка */}
        {!loading && error && (
          <Card className="border-0 text-center" style={{ borderRadius: '0.875rem', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
            <Card.Body className="py-5">
              <p className="text-muted mb-3" style={{ fontSize: '0.9375rem' }}>
                {error}
              </p>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => loadHistory(0, false)}
                style={{ borderRadius: '0.5rem', fontWeight: 500 }}
              >
                Попробовать снова
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Пустое состояние */}
        {!loading && !error && history.length === 0 && (
          <Card
            className="border-0 text-center"
            style={{
              borderRadius: '0.875rem',
              boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
              background: '#fff',
            }}
          >
            <Card.Body className="py-5">
              <p
                className="fw-semibold mb-1"
                style={{ color: '#1e2a4a', fontSize: '0.9375rem' }}
              >
                Вы ещё не выполняли упражнения
              </p>
              <p className="text-muted mb-3" style={{ fontSize: '0.85rem', lineHeight: 1.55 }}>
                Начните с любого — прогресс появится здесь после первого занятия
              </p>
              <Button
                size="sm"
                style={{
                  background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: 600,
                }}
                onClick={() => navigate('/student/exercises')}
              >
                Выбрать упражнение
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Список записей */}
        {!loading && !error && history.length > 0 && (
          <>
            <div className="d-flex flex-column gap-2">
              {history.map((item) => (
                <HistoryItem key={item.history_id} item={item} onAction={handleAction} />
              ))}
            </div>

            {/* Кнопка "Загрузить ещё" */}
            {hasMore && (
              <div className="d-flex justify-content-center mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{ borderRadius: '0.5rem', fontWeight: 500, minWidth: '160px' }}
                >
                  {loadingMore ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Загрузка...
                    </>
                  ) : (
                    'Загрузить ещё'
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
}
